import { useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { filter } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AssistantShortDTO } from '../../../api/gen/base'
import { useInfiniteAssistantListQuery } from '../../../api/hooks/useInfiniteAssistantListQuery'
import { GuestMode } from '../../../components/GuestMode'
import { Notistack } from '../../../components/Notistack'
import { RequestInput } from '../../../components/RequestInput'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import { ASSISTANT_SHOP_ROUTE } from '../../../constants/routing.constants'
import { DemoHiDialog } from '../../../dialogs/DemoHiDialog'
import { WebPushPermissionDeniedDialog } from '../../../dialogs/WebPushPermissionDeniedDialog'
import { WebPushPermissionRequestDialog } from '../../../dialogs/WebPushPermissionRequestDialog'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { useWebPushNotifications } from '../../../providers/WebPushNotificationsProvider/useWebPushNotifications'
import { useProfileStore } from '../../../store/storeProfile'
import { useWrapperStore } from '../../../store/storeWrapper'
import { theme } from '../../../theme'
import { AgentOrAssistantFeaturesList } from './components/AgentOrAssistantFeaturesList'
import { AssistantList } from './components/AssistantList'
import { CloudText } from './components/CloudText'
import { HelpButton } from './components/HelpButton'
import { MobileHelpButton } from './components/MobileHelpButton'
import { ModelsCarousel } from './components/ModelsCarousel'

export const MainPage = () => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [isShowDemoRegistrationDialog, setIsShowDemoRegistrationDialogStorage] = useLocalStorage(
        'isShowDemoRegistrationDialog',
        null,
    )
    const requestInputRef = useRef(null)
    const [openDemoHiDialog, setOpenDemoHiDialog] = useState<boolean>(false)
    const [isOpenPermissionRequestDialog, setIsOpenPermissionRequestDialog] = useState(false)
    const [isOpenPermissionDeniedDialog, setIsOpenPermissionDeniedDialog] = useState(false)
    const [cookies, setCookie] = useCookies()

    const handleCloseDemoHiDialog = () => {
        setIsShowDemoRegistrationDialogStorage(true)
        setOpenDemoHiDialog(false)
    }

    const {
        projectListActive,
        setScrollToRequestInput,
        isScrollToRequestInput,
        isTempUser,
        fetchModelDetail,
        currentAgentNameInDialog,
        currentAssistantIdDialog,
        setOpenSidebarType,
        currentAssistantDataInDialog,
        fetchAssistantSubscriptions,
        assistantsList,
        fetchAssistants,
        stepOnboarding,
        dialogsOpen,
        isDoneUpdateAssistant,
        setIsDoneUpdateAssistant,
    } = useWrapperStore((state) => state)

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchedAfterMount, refetch } =
        useInfiniteAssistantListQuery({
            show_restricted: false,
            order_by: 'popularity',
            popular: true,
        })

    const assistantList = useMemo(
        () =>
            data?.pages.reduce<AssistantShortDTO[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    useEffect(() => {
        if (isDoneUpdateAssistant) {
            refetch()
            setIsDoneUpdateAssistant(false)
        }
    }, [isDoneUpdateAssistant, refetch])

    const handleOpenPermissionDeniedDialog = () => {
        setIsOpenPermissionDeniedDialog(true)
    }

    const handleOpenPermissionRequestDialog = () => {
        setIsOpenPermissionRequestDialog(true)
    }

    const {
        subscribe,
        isSuccess,
        reset,
        isRequestedWebPushPermissionDailyKey,
        isRequestedWebPushPermissionMonthlyKey,
    } = useWebPushNotifications({
        onPermissionDenied: handleOpenPermissionDeniedDialog,
        onRequestPermission: handleOpenPermissionRequestDialog,
    })

    const handleClose = () => {
        setCookie(isRequestedWebPushPermissionDailyKey, true, {
            path: '/',
            expires: dayjs().add(1, 'day').toDate(),
        })

        setIsOpenPermissionRequestDialog(false)
        setIsOpenPermissionDeniedDialog(false)
    }

    const handleDeclinePermissionRequest = () => {
        setCookie(isRequestedWebPushPermissionMonthlyKey, true, {
            path: '/',
            expires: dayjs().add(1, 'month').toDate(),
        })

        setIsOpenPermissionRequestDialog(false)
        setIsOpenPermissionDeniedDialog(false)
    }

    const handleAcceptPermissionRequest = () => {
        subscribe()
        setIsOpenPermissionRequestDialog(false)
    }

    useEffect(() => {
        if (isScrollToRequestInput) {
            requestInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setScrollToRequestInput(false)
        }
    }, [isScrollToRequestInput, requestInputRef])

    useEffect(() => {
        if (isTempUser && !isShowDemoRegistrationDialog) {
            setOpenDemoHiDialog(true)
        }
    }, [isTempUser, isShowDemoRegistrationDialog])

    useEffect(() => {
        if (currentAgentNameInDialog) {
            fetchModelDetail(currentAgentNameInDialog, currentAssistantIdDialog)
        }
    }, [currentAgentNameInDialog, currentAssistantIdDialog])

    useEffect(() => {
        return () => {
            setOpenSidebarType(null)
        }
    }, [])

    useEffect(() => {
        fetchAssistantSubscriptions()
        fetchAssistants()
    }, [])

    const activeAssistant = useMemo(
        () =>
            currentAssistantDataInDialog
                ? {
                      name: currentAssistantDataInDialog.name,
                      title: currentAssistantDataInDialog.header_text,
                      message: currentAssistantDataInDialog.greeting_message,
                      text: currentAssistantDataInDialog.user_actions,
                  }
                : null,
        [currentAssistantDataInDialog],
    )

    const handleLoadMore = useCallback(async () => {
        await fetchNextPage()
    }, [fetchNextPage])

    const isAssistantSelected = !!activeAssistant

    return (
        <>
            <Stack
                sx={{
                    position: 'relative',
                    overflowY: 'hidden',
                    border: isTempUser ? '4px solid #006CFD' : 'none',
                    boxSizing: 'border-box',
                    background: '#FFFFFF',
                    borderRadius: '28px',
                    height: `calc(100dvh - ${isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT}px)`,

                    '&:hover': {
                        borderRadius: '28px',
                    },
                }}>
                <GuestMode />
                <Stack
                    gap="40px"
                    sx={{
                        overflowY: 'auto',
                        padding: isMobile ? '16px' : '32px',
                        paddingTop: '48px',
                        [theme.breakpoints.down('md')]: {
                            gridTemplateColumns: '1fr',
                            rowGap: '32px',
                            height: 'auto',
                        },
                    }}>
                    <Box
                        ref={requestInputRef}
                        textAlign="center"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}>
                        <Stack direction="row" sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                            <Typography variant="h3">
                                👋🏻 Добрый день{isAssistantSelected && ','}
                            </Typography>
                            {isAssistantSelected && (
                                <Typography variant="h3" sx={{ ml: 1 }}>
                                    я{' '}
                                    <span style={{ color: '#006CFD' }}>
                                        {activeAssistant?.name}
                                    </span>
                                </Typography>
                            )}
                        </Stack>

                        {!isAssistantSelected && (
                            <Typography
                                variant={isMobile ? 'h4' : 'h3'}
                                textAlign="center"
                                sx={{ color: '#BDBFC7', mt: '12px' }}>
                                Какую задачу хотите решить?
                            </Typography>
                        )}
                        <ModelsCarousel />

                        {isAssistantSelected && <CloudText title={activeAssistant?.title} />}

                        {projectListActive?.length ? (
                            <RequestInput
                                disabled={stepOnboarding !== 100}
                                isMainPage={true}
                                message={activeAssistant?.message}
                                assistantPresets={activeAssistant?.text}
                                helpButton={
                                    !isAssistantSelected &&
                                    (isMobile ? (
                                        <MobileHelpButton />
                                    ) : (
                                        <HelpButton stepOnboarding={stepOnboarding} />
                                    ))
                                }
                            />
                        ) : (
                            <Grid size={12}>
                                <Skeleton
                                    variant="rectangular"
                                    width="80%"
                                    height={142}
                                    sx={{
                                        margin: '30px auto 119px',
                                        py: '12px',
                                        borderRadius: '24px',
                                    }}
                                />
                            </Grid>
                        )}
                    </Box>

                    {!isMobile && <AgentOrAssistantFeaturesList />}

                    {isFetchedAfterMount &&
                        !!filter(assistantList, { is_favorite: true }).length && (
                            <Stack gap="28px">
                                <Typography variant="h4">Избранные ассистенты </Typography>

                                <AssistantList
                                    list={filter(assistantList, { is_favorite: true })}
                                    loading={isFetching}
                                    hasNextPage={hasNextPage}
                                    onLoadMore={handleLoadMore}
                                />
                            </Stack>
                        )}

                    {isFetchedAfterMount && (
                        <Stack alignItems="center" gap="28px">
                            <Stack
                                sx={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                }}
                                gap={1}>
                                <Typography variant="h4">Популярные ассистенты </Typography>
                                {!isTempUser && assistantsList?.total_count && (
                                    <Link
                                        color="info"
                                        onClick={() => navigate(ASSISTANT_SHOP_ROUTE)}
                                        variant="body1"
                                        sx={{
                                            textDecoration: 'none',
                                            color: '#1B1C1D',
                                            cursor: 'pointer',
                                            marginLeft: 'auto',
                                        }}>
                                        {isMobile
                                            ? `Все (${assistantsList.total_count})`
                                            : `Все ассистенты  (${assistantsList.total_count})`}
                                    </Link>
                                )}
                            </Stack>
                            <AssistantList
                                list={filter(assistantList, { is_favorite: false })}
                                loading={isFetching}
                                hasNextPage={hasNextPage}
                                onLoadMore={handleLoadMore}
                            />
                        </Stack>
                    )}
                </Stack>
            </Stack>

            <DemoHiDialog open={openDemoHiDialog} handleClose={handleCloseDemoHiDialog} />

            <WebPushPermissionRequestDialog
                open={isOpenPermissionRequestDialog && dialogsOpen}
                onClose={handleClose}
                onDecline={handleDeclinePermissionRequest}
                onAccept={handleAcceptPermissionRequest}
            />
            <WebPushPermissionDeniedDialog
                open={isOpenPermissionDeniedDialog && dialogsOpen}
                onClose={handleDeclinePermissionRequest}
            />
            <Notistack
                isError={false}
                open={isSuccess}
                message="Вы успешно подписались на уведомления!"
                onClose={() => reset()}
            />
        </>
    )
}
